import { useContext } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { TenantContext, useMutationWithErrorHandler } from "@nens/ranalib";

import { PublicatonMapId } from "@/features/publications";
import {
  usePublicationCommentListQuery,
  useUpdatePublicationCommentListQuery,
} from "@/queries/query-publication-comment";

interface ToggleCommentReactionArgs {
  commentId: string;
  messageIndex: number;
  emoji: string;
}

export const useToggleCommentReaction = (publicationId: string, mapId: PublicatonMapId) => {
  const { user } = useContext(TenantContext);
  const update = useUpdatePublicationCommentListQuery(publicationId);
  const commentThreadsQuery = usePublicationCommentListQuery(publicationId, mapId);
  const commentThreads = commentThreadsQuery.data?.items ?? null;

  return useMutationWithErrorHandler(
    async ({ commentId, messageIndex, emoji }: ToggleCommentReactionArgs) => {
      if ("id" in user === false) {
        throw new Error("Public users should not be able to add or remove reactions");
      }
      const publicationCommentsApi = getRanaApiV1().PublicationCommentsApi;
      const reactionRequest = {
        publicationId,
        commentId,
        messageIndex,
        emoji,
      };

      const currentComment = commentThreads?.find((comment) => comment.id === commentId);

      if (!currentComment) {
        throw new Error("Comment not found");
      }

      const remove = currentComment.messages[messageIndex].reactions?.some(
        (reaction) => reaction.emoji === emoji && reaction.users?.some(({ id }) => id === user.id)
      );
      const comment = await (remove
        ? publicationCommentsApi.commentDeleteReaction(reactionRequest)
        : publicationCommentsApi.commentAddReaction(reactionRequest));

      await update(comment.id, comment);
    }
  );
};
